import { CSSProperties, FC, useMemo } from "react";
import { Chart } from "../../../components/charts/Chart";
import { LineChart } from "../../../components/charts/LineChart";
import { getTeamColors } from "components/colors";
import {
  collectInningScores,
  ensurePair,
  formatScoreCell,
  parseStsMetrics,
  pickTotals,
} from "./helper";
import { Text } from "components/common/Text";

type BaseballStatus = {
  c?: Record<string, unknown>;
  T1?: { n?: string; name?: string; shortName?: string; kit?: any };
  T2?: { n?: string; name?: string; shortName?: string; kit?: any };
  t1?: { n?: string; name?: string; shortName?: string; kit?: any };
  t2?: { n?: string; name?: string; shortName?: string; kit?: any };
  T1n?: string;
  T2n?: string;
  t1n?: string;
  t2n?: string;
  sts?: string | null;
};

const pickTeamName = (
  status: BaseballStatus,
  role: "home" | "away"
): string => {
  const candidates =
    role === "home"
      ? [
          status?.T1?.name,
          status?.T1?.shortName,
          status?.T1?.n,
          status?.t1?.name,
          status?.t1?.shortName,
          status?.t1?.n,
          status?.T1n,
          status?.t1n,
        ]
      : [
          status?.T2?.name,
          status?.T2?.shortName,
          status?.T2?.n,
          status?.t2?.name,
          status?.t2?.shortName,
          status?.t2?.n,
          status?.T2n,
          status?.t2n,
        ];

  const found = candidates.find(
    (name): name is string => typeof name === "string" && name.trim().length > 0
  );

  return found ?? (role === "home" ? "Home" : "Away");
};

const sumInnings = (innings: ReturnType<typeof collectInningScores>) => {
  if (!innings || innings.length === 0) return null;
  const [homeTotal, awayTotal] = innings.reduce(
    (acc, inning) => {
      const home = inning.scores[0];
      const away = inning.scores[1];
      return [
        acc[0] + (Number.isFinite(home) && home > -1 ? home : 0),
        acc[1] + (Number.isFinite(away) && away > -1 ? away : 0),
      ] as [number, number];
    },
    [0, 0] as [number, number]
  );

  if (homeTotal === 0 && awayTotal === 0) {
    return null;
  }
  return [homeTotal, awayTotal] as [number, number];
};

const firstAvailable = (
  map: Record<string, [number, number] | null>,
  keys: string[]
): [number, number] | null =>
  keys.reduce<[number, number] | null>((acc, key) => {
    if (acc) return acc;
    const value = map[key];
    if (!value) return acc;

    const hasFinite = value.some((n) => Number.isFinite(n));
    return hasFinite ? value : acc;
  }, null);

export const Stats: FC<{ status: BaseballStatus }> = ({ status }) => {
  const { home: homeColor, away: awayColor } = getTeamColors(status as any);

  const scoreboard = useMemo(() => collectInningScores(status?.c), [status?.c]);

  const totalsMap = useMemo(
    () =>
      pickTotals(status?.c, [
        "R",
        "H",
        "E",
        "LOB",
        "LOBs",
        "LOBTotal",
        "ERR",
        "Errors",
      ]),
    [status?.c]
  );

  const runsPair =
    totalsMap.R ??
    sumInnings(scoreboard) ??
    (status?.c ? ensurePair((status.c as any).T) : null);

  const hitsPair = firstAvailable(totalsMap, ["H"]);
  const errorsPair = firstAvailable(totalsMap, ["E", "ERR", "Errors"]);
  const lobPair = firstAvailable(totalsMap, ["LOB", "LOBs", "LOBTotal"]);

  const totalColumns = useMemo(() => {
    const cols: {
      key: string;
      label: string;
      pair: [number, number] | null;
    }[] = [
      {
        key: "R",
        label: "R",
        pair: runsPair ?? null,
      },
    ];

    if (hitsPair) {
      cols.push({ key: "H", label: "H", pair: hitsPair });
    }
    if (errorsPair) {
      cols.push({ key: "E", label: "E", pair: errorsPair });
    }
    if (lobPair) {
      cols.push({ key: "LOB", label: "LOB", pair: lobPair });
    }

    return cols;
  }, [runsPair, hitsPair, errorsPair, lobPair]);

  const summaryCharts = useMemo(() => {
    const descriptors = [
      { title: "RUNS", pair: runsPair },
      { title: "HITS", pair: hitsPair },
      { title: "ERRORS", pair: errorsPair },
      { title: "LEFT ON BASE", pair: lobPair },
    ] as const;

    return descriptors.filter(
      (item) =>
        item.pair &&
        item.pair.some((value) => Number.isFinite(value) && value > 0)
    ) as { title: string; pair: [number, number] }[];
  }, [errorsPair, hitsPair, lobPair, runsPair]);

  const trendCharts = useMemo(() => {
    const descriptors = [
      { title: "RUNS", pair: runsPair },
      { title: "HITS", pair: hitsPair },
      { title: "ERRORS", pair: errorsPair },
      { title: "LEFT ON BASE", pair: lobPair },
    ] as const;

    return descriptors.filter(
      (item) =>
        item.pair &&
        item.pair.some((value) => Number.isFinite(value) && value > 0)
    ) as { title: string; pair: [number, number] }[];
  }, [errorsPair, hitsPair, lobPair, runsPair]);

  const homeTeamName = pickTeamName(status, "home");
  const awayTeamName = pickTeamName(status, "away");

  const stsMetrics = useMemo(() => parseStsMetrics(status?.sts), [status?.sts]);

  const displayedMetrics = useMemo(() => {
    if (!stsMetrics || stsMetrics.length === 0) return [];

    const alwaysInclude = new Set(["PITCHES", "BALLS", "STRIKES"]);

    return stsMetrics
      .filter((metric) => {
        if (alwaysInclude.has(metric.label)) return true;
        return Number.isFinite(metric.value) && metric.value > 0;
      })
      .slice(0, 12);
  }, [stsMetrics]);

  const hasScoreboard =
    scoreboard.length > 0 ||
    totalColumns.some((col) => col.pair && col.pair.some((n) => n !== 0));

  const rootClass = "flex w-full flex-col items-center gap-[2vw]";
  const rootStyle = useMemo(
    () =>
      ({
        "--home-color": homeColor,
        "--away-color": awayColor,
      } as CSSProperties),
    [homeColor, awayColor]
  );

  return (
    <div className={rootClass} style={rootStyle}>
      {summaryCharts.length > 0 && (
        <div className="flex w-full flex-row justify-center gap-[2vw]">
          {summaryCharts.map((chart) => (
            <Chart
              key={chart.title}
              title={chart.title}
              homeValue={chart.pair[0]}
              awayValue={chart.pair[1]}
            />
          ))}
        </div>
      )}

      {displayedMetrics.length > 0 && (
        <div className="flex w-full flex-col gap-[1vw]">
          <h3 className="m-0 font-bold tracking-[0.08em] text-statisticTitle">
            <Text variant="chartTitle">LIVE COUNTS</Text>
          </h3>
          <div className="grid w-full gap-[1vw] [grid-template-columns:repeat(auto-fit,minmax(15vw,1fr))]">
            {displayedMetrics.map((metric) => (
              <div
                key={metric.key}
                className="flex flex-col items-center justify-center rounded-[0.8vw] border px-[1vw] py-[1.2vw] text-center shadow-[0_0.5vw_1.5vw_rgba(0,0,0,0.25)] border-border bg-darkestBlue"
              >
                <Text
                  variant="chartValue"
                  className="font-extrabold text-white text-outline"
                >
                  {metric.value}
                </Text>
                <Text
                  variant="chartValue"
                  className="mt-[0.4vw] font-semibold tracking-[0.06em]"
                  style={{ color: "rgba(221, 228, 255, 0.8)" }}
                >
                  {metric.label}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasScoreboard && (
        <div className="w-full overflow-x-auto">
          <div className="inline-block min-w-full overflow-hidden rounded-[0.75vw] border border-border bg-darkestBlue">
            <table className="w-full border-collapse font-semibold text-white">
              <thead>
                <tr>
                  <th className="min-w-[18vw] border-b border-r border-border text-statisticTitle px-[1.2vw] py-[0.9vw] text-left uppercase tracking-[0.05em] last:border-r-0">
                    <Text variant="chartTitle">TEAM</Text>
                  </th>
                  {scoreboard.map((inning) => (
                    <th
                      key={`head-${inning.key}`}
                      className="border-b border-r border-border text-statisticTitle px-[1.2vw] py-[0.9vw] uppercase tracking-[0.05em] last:border-r-0"
                    >
                      <Text variant="chartValue">{inning.label}</Text>
                    </th>
                  ))}
                  {totalColumns.map((col) => (
                    <th
                      key={`head-${col.key}`}
                      className="border-b border-r border-border text-statisticTitle px-[1.2vw] py-[0.9vw] uppercase tracking-[0.05em] last:border-r-0"
                    >
                      <Text variant="chartValue">{col.label}</Text>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="[&>tr:last-child>td]:border-b-0">
                <tr>
                  <td
                    className="border-b border-r border-border px-[1.2vw] py-[1vw] text-left font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{
                      borderLeft: `4px solid ${homeColor}`,
                    }}
                  >
                    <Text variant="chartTitle">{homeTeamName}</Text>
                  </td>
                  {scoreboard.map((inning) => (
                    <td
                      key={`home-${inning.key}`}
                      className="border-b border-r border-border px-[1vw] py-[1vw] text-center text-white last:border-r-0"
                    >
                      <Text variant="chartValue">
                        {formatScoreCell(inning.scores[0])}
                      </Text>
                    </td>
                  ))}
                  {totalColumns.map((col) => (
                    <td
                      key={`home-total-${col.key}`}
                      className="border-b border-r border-border px-[1vw] py-[1vw] text-center text-white last:border-r-0"
                    >
                      <Text variant="chartValue">
                        {formatScoreCell(col.pair ? col.pair[0] : NaN)}
                      </Text>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td
                    className="border-b border-r border-border px-[1.2vw] py-[1vw] text-left font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{
                      borderLeft: `4px solid ${awayColor}`,
                    }}
                  >
                    <Text variant="chartTitle">{awayTeamName}</Text>
                  </td>
                  {scoreboard.map((inning) => (
                    <td
                      key={`away-${inning.key}`}
                      className="border-b border-r border-border px-[1vw] py-[1vw] text-center text-white last:border-r-0"
                    >
                      <Text variant="chartValue">
                        {formatScoreCell(inning.scores[1])}
                      </Text>
                    </td>
                  ))}
                  {totalColumns.map((col) => (
                    <td
                      key={`away-total-${col.key}`}
                      className="border-b border-r border-border px-[1vw] py-[1vw] text-center text-white last:border-r-0"
                    >
                      <Text variant="chartValue">
                        {formatScoreCell(col.pair ? col.pair[1] : NaN)}
                      </Text>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {trendCharts.length > 0 && (
        <div className="flex w-full flex-col gap-[2vw]">
          {trendCharts.map((chart) => (
            <LineChart
              key={`trend-${chart.title}`}
              title={`${chart.title}`}
              homeValue={chart.pair[0]}
              awayValue={chart.pair[1]}
            />
          ))}
        </div>
      )}
    </div>
  );
};
